// SlideDeckPlayer — promoted from learner-app/components/slide_deck
// to @flowgenix/shared-ui (Phase 2 / Step C-3.3 Pass 2).
//
// Renderer for the `slide_deck_player` widget kind. Renders a converted-
// to-PDF deck a slide at a time via pdfjs-dist, with interactive
// question / clickable-zone overlays at slides flagged in
// scene.domain_metadata.interactions.
//
// Decoupling done in Pass 2:
//   • `slideDeckAPI` is now an injectable prop instead of a singleton
//     import from learner-app/services/api. When omitted (Atlas Catalog
//     preview), the player runs in "preview" mode: no backend round-
//     trips, local-only interaction triggering via the `interactions`
//     prop.
//   • `pdfjsLib` is also injectable (defaults to the bundled
//     pdfjs-dist) so test environments and consumers that pin a
//     specific build can swap in a mock.
//   • `fileUrl` is computed via `slideDeckAPI.fileUrl(deckId)` when
//     available, otherwise the consumer can pass `fileUrl` directly.
//
// pdfjs-dist is a peer dependency of @flowgenix/shared-ui (declared in
// shared/package.json). Consumers that render this widget must install
// pdfjs-dist themselves; the version is pinned to match learner-app
// to keep the worker URL handshake correct.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import './SlideDeckPlayer.css';

// Overlay components — loaded from the learner-app overlays package.
// These are resolved at runtime; consumers that bundle the shared widget
// alongside learner-app will find the files on the expected relative path.
// If not available (e.g. Atlas preview), the components degrade gracefully.
let PauseAndReflectOverlay = null;
let ClickableZoneOverlay = null;
let ConfidenceRatingOverlay = null;
let PredictionOverlay = null;
let BranchingOverlay = null;
let HintRevealOverlay = null;
try {
  PauseAndReflectOverlay = require('../../learner-app/src/components/overlays/PauseAndReflectOverlay').default;
} catch (_e) { /* optional — degrades to generic card */ }
try {
  ClickableZoneOverlay = require('../../learner-app/src/components/overlays/ClickableZoneOverlay').default;
} catch (_e) { /* optional */ }
try {
  ConfidenceRatingOverlay = require('../../learner-app/src/components/overlays/ConfidenceRatingOverlay').default;
} catch (_e) { /* optional */ }
try {
  PredictionOverlay = require('../../learner-app/src/components/overlays/PredictionOverlay').default;
} catch (_e) { /* optional */ }
try {
  BranchingOverlay = require('../../learner-app/src/components/overlays/BranchingOverlay').default;
} catch (_e) { /* optional */ }
try {
  HintRevealOverlay = require('../../learner-app/src/components/overlays/HintRevealOverlay').default;
} catch (_e) { /* optional */ }

// Heavy dep is loaded lazily so that consumers that never instantiate
// the widget (e.g. hub-app, which has no pdfjs-dist install) don't pay
// the bundle cost. The dynamic import is also what lets Vite tree-
// shake the dep out of those builds.
let _pdfjsLibPromise = null;
function loadPdfJs() {
  if (_pdfjsLibPromise) return _pdfjsLibPromise;
  _pdfjsLibPromise = import('pdfjs-dist/build/pdf').then((mod) => {
    const lib = mod && (mod.default || mod);
    const version = lib.version || '4.5.136';
    if (typeof window !== 'undefined' && lib.GlobalWorkerOptions) {
      lib.GlobalWorkerOptions.workerSrc =
        `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.js`;
    }
    return lib;
  });
  return _pdfjsLibPromise;
}

function indexInteractions(interactions = []) {
  const bySlide = {};
  interactions.forEach((interaction) => {
    const idx = Number.isFinite(interaction?.slide_index)
      ? interaction.slide_index
      : Number(interaction?.slide ?? -1);
    if (!Number.isFinite(idx) || idx < 0) return;
    if (!bySlide[idx]) bySlide[idx] = [];
    bySlide[idx].push(interaction);
  });
  return bySlide;
}

export default function SlideDeckPlayer({
  deckId,
  sessionId,
  fileUrl,
  slideDeckAPI,
  pdfjsLib,
  interactions = [],
  speakerNotes = [],
  onInteraction,
  onComplete,
  showSpeakerNotes = false,
  notesPerSlide = {},
}) {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const pdfDocRef = useRef(null);

  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [activeInteraction, setActiveInteraction] = useState(null);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);
  const [zoneClick, setZoneClick] = useState(null);
  const [loading, setLoading] = useState(true);

  const interactionsBySlide = useMemo(
    () => indexInteractions(interactions),
    [interactions],
  );

  const resolvedUrl = useMemo(() => {
    if (fileUrl) return fileUrl;
    if (slideDeckAPI?.fileUrl && deckId) return slideDeckAPI.fileUrl(deckId);
    return null;
  }, [fileUrl, slideDeckAPI, deckId]);

  useEffect(() => {
    if (!resolvedUrl) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const httpHeaders = (() => {
      try {
        const tok = typeof localStorage !== 'undefined'
          ? localStorage.getItem('scenario_os_token')
          : null;
        return tok ? { Authorization: `Bearer ${tok}` } : {};
      } catch (_e) {
        return {};
      }
    })();

    let loadingTask = null;
    const libPromise = pdfjsLib ? Promise.resolve(pdfjsLib) : loadPdfJs();
    libPromise
      .then((lib) => {
        if (cancelled) return null;
        loadingTask = lib.getDocument({
          url: resolvedUrl,
          withCredentials: false,
          httpHeaders,
        });
        return loadingTask.promise;
      })
      .then((doc) => {
        if (cancelled || !doc) return;
        pdfDocRef.current = doc;
        setPdfDoc(doc);
        setPageCount(doc.numPages || 0);
        setCurrentIdx(0);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(`Could not load slide deck: ${e?.message || e}`);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      try { loadingTask?.destroy?.(); } catch (_e) { /* ignore */ }
      try { pdfDocRef.current?.destroy?.(); } catch (_e) { /* ignore */ }
      pdfDocRef.current = null;
    };
  }, [resolvedUrl, pdfjsLib]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const page = await pdfDoc.getPage(currentIdx + 1);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const containerWidth = canvas.parentElement?.clientWidth || 960;
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(2.5, containerWidth / baseViewport.width);
        const viewport = page.getViewport({ scale });

        const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
        canvas.width = Math.round(viewport.width * dpr);
        canvas.height = Math.round(viewport.height * dpr);
        canvas.style.width = `${Math.round(viewport.width)}px`;
        canvas.style.height = `${Math.round(viewport.height)}px`;

        const ctx = canvas.getContext('2d');
        ctx?.setTransform?.(dpr, 0, 0, dpr, 0, 0);

        try { renderTaskRef.current?.cancel?.(); } catch (_e) { /* ignore */ }

        const renderTask = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
      } catch (e) {
        if (cancelled) return;
        if (e?.name === 'RenderingCancelledException') return;
        setError(`Slide render failed: ${e?.message || e}`);
      }
    })();

    return () => {
      cancelled = true;
      try { renderTaskRef.current?.cancel?.(); } catch (_e) { /* ignore */ }
    };
  }, [pdfDoc, currentIdx]);

  const fetchTriggers = useCallback(async (idx) => {
    if (!sessionId || !slideDeckAPI?.navigate) {
      const local = interactionsBySlide[idx] || [];
      if (local.length) {
        const first = local[0];
        setActiveInteraction(first);
        if (onInteraction) onInteraction(first);
      }
      return;
    }
    try {
      const { data } = await slideDeckAPI.navigate({
        session_id: sessionId,
        slide_index: idx,
      });
      const triggered = data?.triggered_interactions || [];
      if (triggered.length) {
        const first = triggered[0];
        setActiveInteraction(first);
        if (onInteraction) onInteraction(first);
      }
      if (data?.progress != null) setProgress(data.progress);
      if (data?.completed && !completed) {
        setCompleted(true);
        if (onComplete) onComplete();
      }
    } catch (_e) {
      /* navigate ack is best-effort */
    }
  }, [sessionId, slideDeckAPI, interactionsBySlide, onInteraction, onComplete, completed]);

  useEffect(() => {
    if (!pageCount) return;
    fetchTriggers(currentIdx);
  }, [currentIdx, pageCount, fetchTriggers]);

  const goPrev = useCallback(() => {
    if (activeInteraction) return;
    setCurrentIdx((idx) => Math.max(0, idx - 1));
  }, [activeInteraction]);

  const goNext = useCallback(() => {
    if (activeInteraction) return;
    setCurrentIdx((idx) => {
      const next = Math.min((pageCount || 1) - 1, idx + 1);
      if (next === idx && !completed) {
        setCompleted(true);
        if (onComplete) onComplete();
      }
      return next;
    });
  }, [activeInteraction, pageCount, completed, onComplete]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight' || e.key === ' ') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  const handleRespond = useCallback(async (response) => {
    if (!activeInteraction) return;
    if (sessionId && slideDeckAPI?.respond) {
      try {
        await slideDeckAPI.respond({
          session_id: sessionId,
          interaction_id: activeInteraction.interaction_id,
          response,
        });
      } catch {
        /* response submit is best-effort */
      }
    }
    setActiveInteraction(null);
    setZoneClick(null);
  }, [activeInteraction, sessionId, slideDeckAPI]);

  const handleZoneClick = useCallback((evt) => {
    if (!activeInteraction || activeInteraction.interaction_type !== 'clickable_zone') return;
    const target = evt.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = (evt.clientX - rect.left) / Math.max(rect.width, 1);
    const y = (evt.clientY - rect.top) / Math.max(rect.height, 1);
    const clamped = {
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    };
    setZoneClick(clamped);
    handleRespond(clamped);
  }, [activeInteraction, handleRespond]);

  const interactionType = activeInteraction?.interaction_type || activeInteraction?.overlay_type || 'mcq';
  const isZoneInteraction = interactionType === 'clickable_zone';
  const isPauseAndReflect = interactionType === 'pause_and_reflect';
  const isConfidenceRating = interactionType === 'confidence_rating';
  const isPrediction = interactionType === 'prediction';
  const isBranching = interactionType === 'branching';
  const isHintReveal = interactionType === 'hint_reveal';
  const choices = activeInteraction?.metadata?.choices || [];

  if (error) {
    return (
      <div className="slide-deck-player slide-deck-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="slide-deck-player">
      <div className="slide-deck-stage">
        {loading && (
          <div className="slide-deck-loading">Loading slides…</div>
        )}
        <canvas
          ref={canvasRef}
          className="slide-deck-canvas"
          aria-label={`Slide ${currentIdx + 1} of ${pageCount || '?'}`}
        />

        {isZoneInteraction && ClickableZoneOverlay ? (
          <ClickableZoneOverlay
            question={activeInteraction?.metadata?.prompt || activeInteraction?.question || 'Click the correct area.'}
            correctZone={activeInteraction?.metadata?.correct_zone || activeInteraction?.correct_zone}
            markerId={activeInteraction?.interaction_id || activeInteraction?.marker_id}
            sceneId={sessionId}
            domain={null}
            gridSize={activeInteraction?.metadata?.grid_size || activeInteraction?.grid_size}
            onDismiss={() => handleRespond('zone_clicked')}
          />
        ) : isZoneInteraction ? (
          <div
            className="slide-deck-zone-layer"
            onClick={handleZoneClick}
          >
            <div className="slide-deck-zone-prompt">
              {activeInteraction?.metadata?.prompt || 'Click the area that matches.'}
            </div>
            {zoneClick && (
              <div
                className="slide-deck-zone-marker"
                style={{
                  left: `${zoneClick.x * 100}%`,
                  top: `${zoneClick.y * 100}%`,
                }}
              />
            )}
          </div>
        ) : null}

        {activeInteraction && isPauseAndReflect && PauseAndReflectOverlay && (
          <PauseAndReflectOverlay
            question={activeInteraction?.metadata?.prompt || activeInteraction?.question}
            markerId={activeInteraction?.interaction_id || activeInteraction?.marker_id}
            sceneId={sessionId}
            domain={null}
            onDismiss={() => handleRespond('reflected')}
          />
        )}

        {activeInteraction && isConfidenceRating && ConfidenceRatingOverlay && (
          <ConfidenceRatingOverlay
            question={activeInteraction?.metadata?.prompt || activeInteraction?.question}
            markerId={activeInteraction?.interaction_id || activeInteraction?.marker_id}
            sceneId={sessionId}
            domain={null}
            onDismiss={() => handleRespond('rated')}
          />
        )}

        {activeInteraction && isPrediction && PredictionOverlay && (
          <PredictionOverlay
            marker={activeInteraction}
            sceneId={sessionId}
            onClose={() => handleRespond('predicted')}
          />
        )}

        {activeInteraction && isBranching && BranchingOverlay && (
          <BranchingOverlay
            marker={activeInteraction}
            sceneId={sessionId}
            onBranch={() => setActiveInteraction(null)}
            onClose={() => handleRespond('branched')}
          />
        )}

        {activeInteraction && isHintReveal && HintRevealOverlay && (
          <HintRevealOverlay
            marker={activeInteraction}
            sceneId={sessionId}
            onClose={() => handleRespond('hint_completed')}
          />
        )}

        {activeInteraction && !isZoneInteraction && !isPauseAndReflect && !isConfidenceRating
          && !isPrediction && !isBranching && !isHintReveal && (
          <div className="slide-deck-interaction-overlay">
            <div className="slide-deck-interaction-card">
              <p className="slide-deck-interaction-prompt">
                {activeInteraction.metadata?.prompt || 'What do you see?'}
              </p>
              {choices.length > 0 ? choices.map((c, i) => (
                <button
                  key={c.id || i}
                  type="button"
                  className="slide-deck-interaction-choice"
                  onClick={() => handleRespond(c.id || c.text)}
                >
                  {c.text}
                </button>
              )) : (
                <button
                  type="button"
                  className="slide-deck-interaction-choice"
                  onClick={() => handleRespond('acknowledged')}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="slide-deck-controls">
        <button
          type="button"
          className="slide-deck-nav-btn"
          onClick={goPrev}
          disabled={currentIdx <= 0 || !!activeInteraction || loading}
        >
          ← Prev
        </button>
        <div className="slide-deck-indicator" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Slide <strong>{currentIdx + 1}</strong> of {pageCount || '?'}
          {/* notes-3: badge showing note count for the current slide */}
          {notesPerSlide && notesPerSlide[currentIdx] > 0 && (
            <span style={{
              background: '#f59e0b',
              color: '#1e293b',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              padding: '1px 6px',
              minWidth: 16,
              textAlign: 'center',
            }} title={`${notesPerSlide[currentIdx]} note(s) on this slide`}>
              {notesPerSlide[currentIdx]}
            </span>
          )}
        </div>
        <button
          type="button"
          className="slide-deck-nav-btn"
          onClick={goNext}
          disabled={currentIdx >= (pageCount || 1) - 1 || !!activeInteraction || loading}
        >
          Next →
        </button>
        {sessionId && (
          <div className="slide-deck-progress-track">
            <div
              className="slide-deck-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {showSpeakerNotes && speakerNotes?.[currentIdx] && (
        <div className="slide-deck-notes">
          <strong>Speaker notes</strong>
          <p>{speakerNotes[currentIdx]}</p>
        </div>
      )}
    </div>
  );
}

SlideDeckPlayer.kind = 'slide_deck_player';
SlideDeckPlayer.displayName = 'SlideDeckPlayer';
